<script setup>
import { computed, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import InboxSwitcher from 'dashboard/components-next/metaAgent/InboxSwitcher.vue';
import Paywall from 'dashboard/components-next/metaAgent/Paywall.vue';

defineProps({
  headerTitle: { type: String, default: '' },
  isFetching: { type: Boolean, default: false },
  // Pages that manage their own vertical layout (the Playground's transcript +
  // composer) need a definite height. Everything else must be free to grow, or
  // content taller than the viewport spills past the container's bottom
  // padding and the last control ends up flush against the window edge.
  contentFill: { type: Boolean, default: false },
});

const { t } = useI18n();
const route = useRoute();
const { shouldShowPaywall } = usePolicy();

const showSwitcher = ref(false);

const inboxes = useMapGetter('metaAgent/getInboxes');
const uiFlags = useMapGetter('metaAgent/getUIFlags');

const activeInboxName = computed(
  () =>
    inboxes.value.find(inbox => inbox.id === Number(route.params.inboxId))
      ?.name || t('META_AGENT.NAME')
);

const showPaywall = computed(() =>
  shouldShowPaywall(FEATURE_FLAGS.META_BUSINESS_AGENT)
);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div
          class="flex flex-col lg:flex-row lg:items-center justify-between w-full gap-4 py-6 lg:gap-2 lg:py-0 lg:h-20"
        >
          <div class="flex items-center gap-3">
            <div v-if="!showPaywall" class="flex items-center gap-2">
              <span class="text-xl font-medium truncate text-n-slate-12">
                {{ activeInboxName }}
              </span>
              <div class="relative group">
                <OnClickOutside @trigger="showSwitcher = false">
                  <Button
                    icon="i-lucide-chevron-down"
                    :variant="showSwitcher ? 'faded' : 'ghost'"
                    color="slate"
                    size="xs"
                    :is-loading="uiFlags.fetchingInboxes"
                    class="rounded-md group-hover:bg-n-slate-3 hover:bg-n-slate-3 [&>span]:size-4"
                    @click="showSwitcher = !showSwitcher"
                  />
                  <InboxSwitcher
                    v-if="showSwitcher"
                    class="absolute ltr:left-0 rtl:right-0 top-9"
                    @close="showSwitcher = false"
                  />
                </OnClickOutside>
              </div>
            </div>
            <div
              v-if="!showPaywall && headerTitle"
              class="flex items-center gap-4"
            >
              <div class="w-0.5 h-4 rounded-2xl bg-n-weak" />
              <span class="text-xl font-medium text-n-slate-12">
                {{ headerTitle }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <slot name="headerActions" />
          </div>
        </div>
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div
        class="w-full max-w-5xl mx-auto pt-4 pb-8"
        :class="contentFill ? 'h-full' : 'min-h-full'"
      >
        <Paywall v-if="showPaywall" />
        <div
          v-else-if="isFetching"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <slot v-else />
      </div>
    </main>
  </section>
</template>
